import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { disableAutoRename } from "@/features/PromptbookEditor/actions/disableAutoRename";
import { PromptbookTable } from "@/features/Promptbooks/components/PromptbookTable/PromptbookTable";
import { useTeamMembers } from "@/features/Teams/hooks/useTeamMembers";
import { useCreateTemplateFromPromptbook } from "@/features/Templates/actions/createTemplateFromPromptbook";
import {
  delete_promptbook_complete_event,
  delete_promptbook_start_event,
  goto_promptbook_event,
  rename_promptbook_event,
  save_promptbook_as_template_event,
} from "@/services/posthog/events";
import { enable_promptbook_templates_flag } from "@/services/posthog/feature_flags";
import { useAnalytics } from "@/services/posthog/hooks";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { DeleteConfirmationModal, RenameItemModal } from "@pufflig/prompt-studio-ui";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import { renameResource } from "../../../Resources/actions/renameResource";
import { useResourceSubscription, useWorkspaceResources } from "../../../Resources/hooks/useResources";
import { deletePromptbook } from "../../actions/deletePromptbook";
import { duplicatePromptbook } from "../../actions/duplicatePromptbook";

interface PromptbooksTabProps {
  workspaceId: string;
  teamId?: string;
}

export const PromptbookTab = (props: PromptbooksTabProps) => {
  const { workspaceId, teamId } = props;
  const [promptbookToDelete, setPromptbookToDelete] = useState<string | null>(null);
  const [promptbookToRename, setPromptbookToRename] = useState<string | null>(null);
  const { isOpen: isDeleteModalOpen, onClose: onCloseDeleteModal, onOpen: onOpenDeleteModal } = useDisclosure();
  const { isOpen: isRenameModalOpen, onClose: onCloseRenameModal, onOpen: onOpenRenameModal } = useDisclosure();
  const analytics = useAnalytics();
  const toast = useToast();
  const enablePromptbookTemplates = useFeatureFlagEnabled(enable_promptbook_templates_flag);
  const createTemplateFromPromptbook = useCreateTemplateFromPromptbook();

  const canEdit = true;
  const { navigateToPromptbook } = useAppNavigation();

  const { data: promptbooks, isLoading: isLoadingPromptbooks } = useWorkspaceResources(
    workspaceId as string,
    "promptbook",
  );

  useResourceSubscription();

  const { data: teamMembers } = useTeamMembers(teamId);

  const tableData = promptbooks
    ?.map((promptbook) => ({
      id: promptbook.resource_id,
      name: promptbook.name,
      owner: {
        name:
          teamMembers?.[promptbook.created_by || ""]?.name || teamMembers?.[promptbook.created_by || ""]?.email || "",
        picture: teamMembers?.[promptbook.created_by || ""]?.picture,
      },
      modified: promptbook.modified_at,
    }))
    .sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime());

  return (
    <>
      <PromptbookTable
        data={tableData || []}
        isLoadingData={isLoadingPromptbooks}
        isReadOnly={!canEdit}
        onSaveAsTemplate={
          enablePromptbookTemplates
            ? async (rowId) => {
                const { error } = await createTemplateFromPromptbook({ promptbookId: rowId, teamId });
                if (!error) {
                  analytics.capture(save_promptbook_as_template_event, { from: "promptbook_table" });

                  toast({
                    title: "Template created",
                    description: "Your template has been created successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              }
            : undefined
        }
        onSelectRow={(rowId) => {
          analytics.capture(goto_promptbook_event, { from: "promptbook_table" });
          navigateToPromptbook(rowId);
        }}
        onDeletePromptbook={(promptbookId) => {
          analytics.capture(delete_promptbook_start_event, { from: "promptbook_table" });
          setPromptbookToDelete(promptbookId);
          onOpenDeleteModal();
        }}
        onDuplicatePromptbook={async (promptbookId) => {
          const { newPromptbookId } = await duplicatePromptbook(promptbookId, teamId);
          if (newPromptbookId) navigateToPromptbook(newPromptbookId);
        }}
        onRenamePromptbook={(promptbookId) => {
          analytics.capture(delete_promptbook_start_event, { from: "promptbook_table" });
          setPromptbookToRename(promptbookId);
          onOpenRenameModal();
        }}
      />
      <DeleteConfirmationModal
        headline="Delete Promptbook"
        notificationText="You are about to delete this Promptbook. This action cannot be undone. Are you sure?"
        buttonText="Delete Promptbook"
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setPromptbookToDelete(null);
          onCloseDeleteModal();
        }}
        onDelete={async () => {
          if (!promptbookToDelete) return;
          analytics.capture(delete_promptbook_complete_event, { from: "modal" });
          await deletePromptbook(promptbookToDelete);
        }}
      />
      <RenameItemModal
        title="Rename Promptbook"
        buttonText="Rename Promptbook"
        defaultName={tableData?.find((promptbook) => promptbook.id === promptbookToRename)?.name || ""}
        inputPlaceholder="Rename Promptbook"
        isOpen={isRenameModalOpen}
        onClose={onCloseRenameModal}
        onRename={async (newName) => {
          if (!promptbookToRename) return;
          analytics.capture(rename_promptbook_event, { from: "modal" });
          await renameResource(promptbookToRename, newName);
          await disableAutoRename(promptbookToRename);
        }}
      />
    </>
  );
};
